import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SuggestInput, { INPUT_PROBLEM_CLASS, matchSuggestions } from '../ui/SuggestInput';
import { BTN } from '../../constants/buttons';
import { SHEET_BOTTOM, SHEET_Z } from '../../constants/overlayLayers';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';

/**
 * ONE cell of `WeekTimecardGrid` — at rest a plain centred value, on a click
 * the control IN PLACE at the cell's width (owner-approved mock, 2026-10-25:
 * "no wall of boxes at rest"). Two primitives:
 *
 *  - `CellValue` — the resting cell. Editable → a real `<button>` (keyboard,
 *    focus ring, a hover box); read-only → a `<div>`. Blank shows a faint `·`
 *    with an sr-only "blank". A CONFLICT carries the house amber pair
 *    (`text-amber-700 dark:text-amber-400`), an amber outline + soft fill, and
 *    the house ▲ — a SHAPE, so colour is never the only channel. An OWN value
 *    (not following the unit) carries a small dot and says so in its title.
 *  - `CellEditor` — the in-place control: `time` / `hours` / `count` / `text`
 *    inputs commit on blur or Enter and cancel on Escape; a `<select>` commits
 *    on change. 📱 44px tall below `md`.
 *
 * 📏 DENSITY lives HERE so every grid inherits it (owner 2026-10-25: "make both
 * the unit and my timesheets fit on a single normal laptop screen"): a 28px row
 * from `md` (a hit area well over 24px), the 44px tap target below it; values
 * stay `text-sm`.
 *
 * It decides nothing: the grid's caller says what a cell shows, whether it is
 * editable and what a commit means.
 */

const BASE =
  'relative flex w-full items-center justify-center gap-1 min-h-[44px] md:min-h-[1.75rem] px-1 ' +
  'rounded-md text-sm tabular-nums text-center max-md:justify-start max-md:text-left';
const EDITABLE =
  'hover:bg-slate-500/[0.12] hover:outline hover:outline-1 hover:outline-slate-400/70 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const CONFLICT =
  'outline outline-1 outline-amber-500 bg-amber-50 text-amber-700 ' +
  'hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/40 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500';
// An unsaved edit: a TEXT colour only, no fill (owner 2026-10-26: "I dont like the bg colour change").
const DRAFTED = 'text-blue-600 dark:text-blue-400';
// A REQUIRED value still blank on a started/expected working day (owner
// 2026-10-25): a faint red OUTLINE (a shape, not colour alone) + title "Required".
const REQUIRED = 'outline outline-1 outline-red-600/60 dark:outline-red-400/70';
// EDITED SINCE SUBMITTED (owner-approved mock 2026-10-29): a soft amber fill + an amber UNDERLINE
// (a shape as well as a colour). Only a cell the caller flags `edited`; every other cell is unchanged.
const EDITED = 'bg-amber-50 shadow-[inset_0_-2px_0_#f59e0b] dark:bg-amber-900/20';
const TONE = {
  own: 'text-gray-900 dark:text-gray-50',
  follow: 'text-gray-600 dark:text-gray-300',
  computed: 'italic text-gray-500 dark:text-gray-400',
  mixed: 'italic text-gray-500 dark:text-gray-400',
  // A buyer's value on the crew screen: read-only, greyed, with a lock (2026-10-25).
  locked: 'text-gray-500 dark:text-gray-400',
  // Calculated overtime (2026-10-26, the owner-approved mock): a manager OVERRIDE of a
  // calculated figure is blue; a warning the server computed (rest under 11h) is amber.
  override: 'font-semibold text-blue-700 dark:text-blue-300',
  warn: 'font-medium text-amber-700 dark:text-amber-400',
};

/**
 * THE cursor of a live cell (owner 2026-10-25): a TYPED value (time, hours,
 * Day #, text) shows the text caret; a PICKED or TOGGLED one (a select, a tick)
 * or a conflict that opens the chooser shows the pointer. A read-only cell has
 * no cursor class and no hover box at all.
 */
export const TYPED_KINDS = Object.freeze(['time', 'hours', 'count', 'text']);
export function cellCursor(kind, opensChooser) {
  return !opensChooser && TYPED_KINDS.includes(kind) ? 'cursor-text' : 'cursor-pointer';
}

export function CellValue({ cell, bold, label, onActivate, testId, kind, opensChooser = false }) {
  const conflict = !!cell.conflict;
  const tone = TONE[cell.tone] || TONE.follow;
  const cls = [
    BASE,
    conflict ? CONFLICT : `${cell.drafted && !cell.strike ? DRAFTED : tone} ${cell.editable ? EDITABLE : ''} ${cell.required ? REQUIRED : ''}`,
    cell.editable || (conflict && opensChooser) ? cellCursor(kind, conflict && opensChooser) : '',
    bold && !conflict && cell.tone !== 'computed' ? 'font-semibold' : '',
    // Spread, never `cond ? EDITED : ''`: an unflagged cell keeps its class string byte for byte.
    ...(cell.edited && !conflict && !cell.drafted ? [EDITED] : []),
  ].join(' ');
  const body = (
    <>
      {conflict && <span aria-hidden="true">▲</span>}
      {cell.text == null
        ? <><span aria-hidden="true" className="text-gray-400 dark:text-gray-500">·</span><span className="sr-only">blank</span></>
        : <span className={`min-w-0 truncate px-0.5 ${cell.strike ? 'line-through' : ''}`}>{cell.text}</span>}
      {cell.badge && <span className="shrink-0 text-[10px] font-normal not-italic text-indigo-600 dark:text-indigo-300">{cell.badge}</span>}
      {cell.locked && (
        <svg aria-hidden="true" data-lock="true" viewBox="0 0 16 16" className="absolute right-1 top-1 h-2.5 w-2.5 fill-current text-gray-500 dark:text-gray-400"><path d="M4 7V5a4 4 0 118 0v2h1v8H3V7h1zm2 0h4V5a2 2 0 10-4 0v2z" /></svg>
      )}
      {cell.own && !conflict && !cell.locked && (
        <span aria-hidden="true" className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-blue-600 dark:bg-blue-400" />
      )}
      {cell.srNote && <span className="sr-only">{` ${cell.srNote}`}</span>}
      {cell.edited && !conflict && !cell.drafted && <span className="sr-only"> changed since submitted</span>}
    </>
  );
  if (!cell.editable && !conflict) {
    return <div className={cls} title={cell.title} data-testid={testId} data-edited={cell.edited && !cell.drafted ? 'true' : undefined}>{body}</div>;
  }
  return (
    <button
      type="button"
      className={cls}
      title={cell.title}
      aria-label={`${label}: ${cell.text == null ? 'blank' : cell.text}${conflict ? ', differs from the unit timecard' : ''}${cell.srNote ? `, ${cell.srNote}` : ''}`}
      data-testid={testId}
      data-conflict={conflict ? 'true' : undefined}
      data-drafted={cell.drafted ? 'true' : undefined}
      data-edited={cell.edited && !cell.drafted && !conflict ? 'true' : undefined}
      data-required={cell.required && !conflict ? 'true' : undefined}
      onClick={onActivate}
    >
      {body}
    </button>
  );
}

// 📱 16px below `md` (2026-10-28 phone audit, the Working day <select>): iOS zooms on focus into any
// control under 16px. md+ keeps `text-sm`.
const EDITOR =
  'block w-full min-h-[44px] md:min-h-0 md:h-[1.75rem] rounded-md border border-blue-400 bg-white px-1 text-center max-md:px-2 max-md:text-left text-sm max-md:text-base ' +
  'text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-blue-500 dark:bg-gray-700 dark:text-gray-100';

/** The phone job sheet's words (the Main job row) — the default, so that row stays as it was. */
const JOB_SHEET_COPY = Object.freeze({
  placeholder: 'Type a job or pick one below',
  listLabel: 'Jobs',
  emptyText: (typed) => (typed ? `No match — Done saves “${typed}” as a custom job` : 'No jobs yet — type one'),
});

/**
 * 🔢 `validate(text) → problem | null` (input audit 2026-10-28: "2h" sat in the draft and was refused
 * only at Save): a typed value with a problem is NEVER committed — the editor stays open, says why
 * (`aria-invalid` + the line under the box) and Escape / another cell drops it. Absent = anything goes.
 * `suggest` = the suggestion editor's words + `inputMode` (Travel (h)); absent = the job row's.
 */
export function CellEditor({ kind, raw, options, label, onCommit, onCancel, testId, validate, suggest }) {
  const [value, setTyped] = useState(raw == null ? '' : String(raw));
  const phone = useIsMobileOrderViewport(); // below `md`: the job picker is a bottom sheet
  const ref = useRef(null);
  const done = useRef(false);
  // Did the user TYPE (any change event)? Handed to `onCommit` as its second argument so a caller
  // can tell an edited equal value from an untouched open-and-leave (ruling 1A, Pre-call / Final out).
  const edited = useRef(false);
  const setValue = (v) => { edited.current = true; setTyped(v); }; // every typed change, every editor
  const problem = validate ? validate(value) : null;
  const copy = suggest || JOB_SHEET_COPY;
  // ONE click (or Enter/Space on the cell) opens a select's list (owner
  // 2026-10-25: "takes two clicks for the drop down to appear. make it one
  // click"). The editor mounts in the same gesture, so the click's user
  // activation is still live for `showPicker()`; where it is missing or
  // refused the select is simply focused (Alt+↓ / a click opens it). A typed
  // input takes the caret the same way.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (kind === 'suggest') { const input = el.tagName === 'INPUT' ? el : el.querySelector('input'); if (input) { input.focus(); input.select(); } return; }
    el.focus();
    if (kind === 'select' && typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch (e) { /* no activation: focus is enough */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (v) => {
    if (done.current) return;
    if (validate && validate(v)) return; // refused at the field — never coerced, never drafted
    done.current = true;
    onCommit(v, edited.current);
  };
  const problemId = `${testId}-problem`;
  const problemLine = problem ? (
    <span id={problemId} role="alert" data-testid={problemId}
      className={INPUT_PROBLEM_CLASS}>
      {problem}
    </span>
  ) : null;
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    onCancel();
  };

  if (kind === 'suggest' && phone) {
    // 📱 PHONE (owner 2026-10-27: the job list was squeezed to one day's width — "obviously a big
    // issue"): a BOTTOM SHEET, the shape `ui/Dropdown` uses below `md` (same SHEET_Z / SHEET_BOTTOM,
    // dimmed backdrop that closes on CLICK), with a full-width box and `matchSuggestions` filtering.
    const matches = matchSuggestions(options, value);
    return createPortal(
      <>
        <div aria-hidden="true" className="fixed inset-0 bg-black/40" style={{ zIndex: SHEET_Z }} onClick={() => cancel()} />
        <div role="dialog" aria-label={label} data-testid={`${testId}-sheet`}
          style={{ position: 'fixed', left: 8, right: 8, bottom: SHEET_BOTTOM, zIndex: SHEET_Z, maxHeight: '70vh' }}
          className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">{label}</div>
            <input ref={ref} type="text" value={value} aria-label={label} data-testid={testId} autoComplete="off"
              placeholder={copy.placeholder}
              {...(copy.inputMode ? { inputMode: copy.inputMode } : {})}
              {...(problem ? { 'aria-invalid': 'true', 'aria-describedby': problemId } : {})}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(value); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
              className="h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-base text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100" />
            {problem && <p id={problemId} role="alert" data-testid={problemId} className="mt-1 text-sm text-red-700 dark:text-red-300">{problem}</p>}
          </div>
          <ul role="listbox" aria-label={copy.listLabel} className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            {matches.map((o) => (
              <li key={o} role="option" aria-selected={o === value}>
                <button type="button" onClick={() => commit(o)}
                  className={`flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 ${o === value ? 'font-semibold' : ''}`}>
                  <span className="w-3 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true">{o === value ? '✓' : ''}</span>
                  <span className="min-w-0 break-words">{o}</span>
                </button>
              </li>
            ))}
            {matches.length === 0 && (
              <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {copy.emptyText(value.trim())}
              </li>
            )}
          </ul>
          <div className="flex justify-evenly gap-2 border-t border-gray-200 p-2 dark:border-gray-700">
            <button type="button" className={BTN.secondary} onClick={() => cancel()}>Cancel</button>
            {value !== '' && <button type="button" className={BTN['danger-soft']} onClick={() => commit('')}>Clear</button>}
            <button type="button" className={BTN.primary} onClick={() => commit(value)}>Done</button>
          </div>
        </div>
      </>,
      document.body
    );
  }
  if (kind === 'suggest') {
    // FREE TEXT + SUGGESTIONS (owner 2026-10-27, Main job/task: "type a custom job … auto suggest so
    // if I type in 'Rain' the list will show 'Rain Rig' …") — THE shared `ui/SuggestInput`. Enter
    // (list closed) or leaving the cell saves; Escape (list closed) cancels. The list portals to
    // <body> and keeps focus in the input, so picking never blurs the cell.
    return (
      <div
        ref={ref}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.defaultPrevented) return; // the open list claimed it
          if (e.key === 'Enter') { e.preventDefault(); commit(value); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        onBlur={(e) => {
          const next = e.relatedTarget;
          if (next && (e.currentTarget.contains(next) || (next.closest && next.closest('[data-suggest-list]')))) return;
          commit(value);
        }}
      >
        <SuggestInput value={value} onChange={setValue} options={options} ariaLabel={label} testId={testId}
          inputClassName={EDITOR} listLabel={copy.listLabel}
          {...(copy.inputMode ? { inputMode: copy.inputMode } : {})}
          {...(copy.enterPassesThrough ? { enterPassesThrough: true } : {})}
          {...(validate ? { accept: validate } : {})} />
      </div>
    );
  }
  if (kind === 'select') {
    return (
      <select
        ref={ref}
        aria-label={label}
        data-testid={testId}
        className={EDITOR}
        value={value}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => cancel()}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
      >
        {options.map(({ value: v, label: text }) => <option key={v} value={v}>{text}</option>)}
      </select>
    );
  }
  const input = (
    <input
      ref={ref}
      aria-label={label}
      data-testid={testId}
      className={EDITOR}
      type={kind === 'time' ? 'time' : 'text'}
      // Day # is a whole number: the numeric keypad, not the decimal one (input audit 2026-10-28).
      inputMode={kind === 'hours' ? 'decimal' : kind === 'count' ? 'numeric' : undefined}
      maxLength={kind === 'text' ? 2000 : undefined}
      value={value}
      {...(problem ? { 'aria-invalid': 'true', 'aria-describedby': problemId } : {})}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => commit(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(value); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
    />
  );
  // A validated editor carries its refusal line UNDER the box (absolute: the 28px row does not grow).
  return validate ? <div className="relative">{input}{problemLine}</div> : input;
}
